import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../api';
import styles from '../assets/guardianRetentionResponse.module.css';

const GuardianRetentionResponse = () => {
  const { token, response } = useParams();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleRetentionResponse = async () => {
      try {
        console.log(`📧 Processing retention response: ${response} for token: ${token}`);

        // Call the retention response endpoint
        const apiResponse = await api.get(
          `/guardianretentionrequests/respond/${token}/${response}`
        );

        if (apiResponse.data.success) {
          setResult({
            action: response,
            message: apiResponse.data.message,
          });
          toast.success(apiResponse.data.message);
        } else {
          setError(apiResponse.data.message || 'Failed to process your response');
          toast.error(apiResponse.data.message || 'Failed to process your response');
        }
      } catch (err) {
        console.error('Error processing retention response:', err);
        const errorMessage =
          err.response?.data?.message ||
          'An error occurred while processing your response. Please contact support.';
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setProcessing(false);
      }
    };

    if (token && response) {
      handleRetentionResponse();
    } else {
      setError('Invalid retention link');
      setProcessing(false);
    }
  }, [token, response]);

  if (processing) {
    return (
      <div className={styles.container}>
        <div className={styles.processingBox}>
          <div className={styles.spinner}></div>
          <h2>Processing your response...</h2>
          <p>Please wait while we update your account preferences.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorBox}>
          <div className={styles.errorIcon}>❌</div>
          <h2>Error Processing Request</h2>
          <p className={styles.errorMessage}>{error}</p>
          <div className={styles.actions}>
            <button
              onClick={() => navigate('/login')}
              className={styles.loginButton}
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Success response
  const isRetain = response === 'retain';

  return (
    <div className={styles.container}>
      <div className={styles.successBox}>
        <div className={styles.successIcon}>{isRetain ? '✅' : '🗑️'}</div>

        {isRetain ? (
          <>
            <h1 className={styles.title}>Account Retained</h1>
            <h2 className={styles.subtitle}>Thank you for staying with us!</h2>
            <p className={styles.description}>
              We're glad to have you continue as part of the Global Guardians community.
              Your account will remain active and you can continue to access all services.
            </p>
            <div className={styles.infoBox}>
              <h3>What's Next?</h3>
              <ul className={styles.nextSteps}>
                <li>Your account remains fully active</li>
                <li>You can continue searching for properties</li>
                <li>All your documents and application history are preserved</li>
                <li>You'll continue to receive email updates about available properties</li>
              </ul>
            </div>
            <div className={styles.actions}>
              <button
                onClick={() => navigate('/login')}
                className={styles.primaryButton}
              >
                Login to Your Account
              </button>
              <button
                onClick={() => navigate('/')}
                className={styles.secondaryButton}
              >
                Go to Homepage
              </button>
            </div>
          </>
        ) : (
          <>
            <h1 className={styles.title}>Account Deletion Confirmed</h1>
            <h2 className={styles.subtitle}>Your account will be deleted</h2>
            <p className={styles.description}>
              We've received your request to delete your account. Your account and all
              associated data will be permanently removed from our system in accordance
              with GDPR regulations.
            </p>
            <div className={styles.warningBox}>
              <h3>⚠️ Important Information</h3>
              <ul className={styles.deletionInfo}>
                <li>Your account will be permanently deleted within 30 days</li>
                <li>All personal data will be removed from our systems</li>
                <li>This action cannot be undone</li>
                <li>You will no longer receive emails from Global Guardians</li>
                <li>If you change your mind, you'll need to re-register as a new guardian</li>
              </ul>
            </div>
            <div className={styles.infoBox}>
              <h3>What Gets Deleted?</h3>
              <ul className={styles.deletionDetails}>
                <li>Personal information (name, email, phone, address)</li>
                <li>Employment history records</li>
                <li>Uploaded documents (passport, payslips, references)</li>
                <li>Application history and notes</li>
                <li>Payment records (except those required for legal/accounting purposes)</li>
              </ul>
            </div>
            <p className={styles.thankYou}>
              Thank you for being part of Global Guardians. We're sorry to see you go.
              If you have any questions, please contact our support team.
            </p>
            <div className={styles.actions}>
              <button
                onClick={() => navigate('/')}
                className={styles.primaryButton}
              >
                Go to Homepage
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default GuardianRetentionResponse;
